import { useRef } from "react";
import { useFrameSequence } from "./useFrameSequence";

interface HeroSectionProps {
  title: string;
  dateTime: string | null;
  location: string | null;
  slug: string;
  frameCount: number;
  heroImageUrl?: string | null;
}

export function HeroSection({ title, dateTime, location, slug, frameCount, heroImageUrl }: HeroSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useFrameSequence({ slug, frameCount, canvasRef, containerRef });

  const formattedDate = dateTime
    ? new Date(dateTime).toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const subtitle = [formattedDate, location].filter(Boolean).join(" · ");

  return (
    <section
      ref={containerRef}
      className="relative flex h-dvh w-full items-center justify-center overflow-hidden"
      style={{ background: "var(--join-dark, #1b1817)" }}
    >
      {frameCount > 0 && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {frameCount === 0 && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={
            heroImageUrl
              ? { backgroundImage: `url(${heroImageUrl})` }
              : {
                  background:
                    "linear-gradient(135deg, var(--join-dark, #1b1817) 0%, color-mix(in srgb, var(--join-dark, #1b1817) 70%, var(--join-primary, #8d6b5e)) 100%)",
                }
          }
        >
          {!heroImageUrl && (
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                backgroundRepeat: "repeat",
                backgroundSize: "128px 128px",
              }}
            />
          )}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#1b1817] to-transparent" />

      <div className="relative z-10 flex flex-col items-center gap-4 px-6 text-center">
        <h1
          className="font-serif text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
          style={{ fontFamily: "var(--join-font-heading, 'Playfair Display', serif)" }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm font-medium tracking-[0.2em] text-white/70 uppercase sm:text-base">
            {subtitle}
          </p>
        )}

        <div className="mt-8 flex flex-col items-center gap-2 animate-bounce">
          <div className="h-8 w-px bg-white/40" />
          <svg
            className="h-4 w-4 text-white/40"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </section>
  );
}
